import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Calculator, HelpCircle, Info, CheckCircle2, RefreshCw, ArrowRight, ChevronRight, Square, Circle, Triangle, Shapes } from 'lucide-react';
import { MaterialCalculator } from '../components/MaterialCalculator';
// Shape options for the calculator — each maps to a distinct area formula.
// 'irregular' uses the same length × width as rectangle so users can
// estimate odd shapes via their bounding box.
type Shape = 'rectangle' | 'circle' | 'triangle' | 'irregular';
const shapeOptions: {
  id: Shape;
  label: string;
  Icon: (props: {
    className?: string;
  }) => JSX.Element;
}[] = [{
  id: 'rectangle',
  label: 'Rectangle',
  Icon: Square
}, {
  id: 'circle',
  label: 'Circle',
  Icon: Circle
}, {
  id: 'triangle',
  label: 'Triangle',
  Icon: Triangle
}, {
  id: 'irregular',
  label: 'Irregular',
  Icon: Shapes
}];
// --- CALCULATOR DATA ---
const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french_drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
const calcMaterials = [{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$67'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$67'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$61'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$44'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$44'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$24'
}, {
  id: 'builders-sand',
  name: 'Builders Sand',
  density: 1.5,
  priceRange: '$73'
}, {
  id: 'gravel-borrow',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$48'
}, {
  id: '58-clear',
  name: '5/8" Crushed Clear',
  density: 1.35,
  priceRange: '$87'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: '125-clear',
  name: '1.25" Crushed Clear',
  density: 1.35,
  priceRange: '$76'
}, {
  id: '34-clean',
  name: '3/4" Crushed Clear',
  density: 1.35,
  priceRange: '$89'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$76'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$60'
}, {
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$48'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$57'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$66'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$61'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$84'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$93'
}, {
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$72'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$152'
}];
// --- QUIZ DATA ---
const quizQuestions = [{
  id: 'project',
  question: 'What type of project are you working on?',
  options: [{
    label: 'Driveway or Parking Area',
    value: 'driveway'
  }, {
    label: 'Drainage (French drain, retaining wall)',
    value: 'drainage'
  }, {
    label: 'Landscaping or Garden Beds',
    value: 'landscape'
  }, {
    label: 'Walkway or Patio Base',
    value: 'walkway'
  }, {
    label: 'Erosion Control or Slope Stabilization',
    value: 'erosion'
  }, {
    label: 'Concrete, Masonry, or Leveling',
    value: 'concrete'
  }, {
    label: 'Grading, Fill, or Raising Low Spots',
    value: 'fill'
  }]
}, {
  id: 'driveway_layer',
  dependsOn: {
    questionId: 'project',
    value: 'driveway'
  },
  question: 'Which layer are you building?',
  options: [{
    label: 'The deep foundation (Base layer)',
    value: 'base',
    result: {
      name: '1.25" Minus',
      desc: 'A coarser crushed rock that compacts into a rock-solid foundation. Often used as the first layer before topping with finer gravel.'
    }
  }, {
    label: 'The top driving surface (Top layer)',
    value: 'top',
    result: {
      name: '5/8" Minus',
      desc: 'Our most popular driveway gravel — packs down tight and creates a smooth, firm driving surface.'
    }
  }, {
    label: 'Budget-friendly option (Recycled material)',
    value: 'budget',
    result: {
      name: 'Recycled Concrete or Recycled Asphalt',
      desc: 'Sustainable, affordable alternatives that perform like virgin rock. Recycled Asphalt even re-binds over time for a semi-hard surface. Great for rural driveways and parking areas.'
    }
  }]
}, {
  id: 'drainage_type',
  dependsOn: {
    questionId: 'project',
    value: 'drainage'
  },
  question: 'What kind of drainage?',
  options: [{
    label: 'Standard French Drain',
    value: 'french',
    result: {
      name: '7/8" Drain Rock',
      desc: 'The Puget Sound standard for residential French drains. Washed clean so water flows freely through it.'
    }
  }, {
    label: 'Heavy-duty / Behind Retaining Wall',
    value: 'heavy',
    result: {
      name: '1.5" Drain Rock or 1.25" Crushed Clear',
      desc: 'Larger stone with bigger voids for maximum water flow in heavy-duty drainage and retaining wall backfill.'
    }
  }]
}, {
  id: 'landscape_type',
  dependsOn: {
    questionId: 'project',
    value: 'landscape'
  },
  question: 'What are you trying to achieve?',
  options: [{
    label: 'Planting grass or new plants',
    value: 'plants',
    result: {
      name: '3-Way Topsoil',
      desc: 'A premium blend of topsoil, compost, and sand — everything your plants need to thrive from day one.'
    }
  }, {
    label: 'Amending heavy clay soil',
    value: 'amend',
    result: {
      name: '50/50 Mix',
      desc: 'Half topsoil, half compost. Packed with organic matter to break up clay and boost soil health.'
    }
  }, {
    label: 'Pure compost for enriching soil',
    value: 'compost',
    result: {
      name: 'Compost',
      desc: 'Rich, fully aged organic compost packed with beneficial microbes and nutrients. Top-dress lawns, enrich raised beds, or boost vegetable garden health.'
    }
  }, {
    label: 'Weed control and moisture retention',
    value: 'weeds',
    result: {
      name: 'Fine, Medium, or Coarse Bark',
      desc: 'Natural PNW bark mulch to protect beds, suppress weeds, and retain moisture. Fine Bark for a polished look, Medium for all-around use, Coarse for maximum longevity.'
    }
  }, {
    label: 'Decorative ground cover or dry creek bed',
    value: 'decorative',
    result: {
      name: 'Streambed Cobble, River Rock, or Lava Rock',
      desc: 'Streambed Cobble for a natural creek bed look, River Rock for smooth organic accents, or Lava Rock for bold reddish-brown color and lightweight coverage. All zero-maintenance.'
    }
  }, {
    label: 'Decorative walking path',
    value: 'path',
    result: {
      name: 'Pea Gravel',
      desc: 'Small, smooth rounded stones for a beautiful, crunching pathway that drains well.'
    }
  }]
}, {
  id: 'walkway_type',
  dependsOn: {
    questionId: 'project',
    value: 'walkway'
  },
  question: 'What is the surface going to be?',
  options: [{
    label: 'Pavers or Flagstone (Need a base)',
    value: 'pavers',
    result: {
      name: '5/8" Minus + Builders Sand',
      desc: 'Use 5/8" Minus as a compacted sub-base, then a layer of Builders Sand to level and set your pavers.'
    }
  }, {
    label: 'Just gravel (Walking on it directly)',
    value: 'gravel',
    result: {
      name: 'Pea Gravel or 5/8" Minus',
      desc: 'Pea gravel for a loose decorative feel, or 5/8" Minus for a firm, packed path.'
    }
  }]
}, {
  id: 'erosion_type',
  dependsOn: {
    questionId: 'project',
    value: 'erosion'
  },
  question: 'What does the area look like?',
  options: [{
    label: 'Steep slope or embankment',
    value: 'steep',
    result: {
      name: '2-4" Quarry Spalls or Large Rocks',
      desc: 'Quarry Spalls (2-4") lock in place on slopes for standard rip-rap. For extreme erosion or shoreline protection, Large Rocks (1-4 man) provide maximum mass and stability — nothing moves them.'
    }
  }, {
    label: 'Gentle slope or ditch lining',
    value: 'gentle',
    result: {
      name: '1.25" Crushed Clear, Streambed Cobble, or 2-4" Quarry Spalls',
      desc: 'For gentle slopes, 1.25" Crushed Clear provides drainage and stability. Streambed Cobble offers a natural, decorative look while controlling erosion. For steeper ditches, step up to 2-4" Quarry Spalls.'
    }
  }, {
    label: 'Severe erosion or shoreline armoring',
    value: 'severe',
    result: {
      name: 'Large Rocks (1-4 Man)',
      desc: 'Massive quarry rocks ranging from 100 lbs to 2,000+ lbs. The ultimate solution for shoreline protection, steep bank armoring, and heavy rip-rap where nothing else will hold. Delivery includes placement.'
    }
  }]
}, {
  id: 'concrete_type',
  dependsOn: {
    questionId: 'project',
    value: 'concrete'
  },
  question: 'What are you doing?',
  options: [{
    label: 'Mixing concrete or mortar',
    value: 'mixing',
    result: {
      name: 'Builders Sand',
      desc: 'Coarse, washed sand with consistent grain size — essential for mixing concrete, mortar, and stucco.'
    }
  }, {
    label: 'Leveling under pavers, a pool, or a shed',
    value: 'leveling',
    result: {
      name: 'Builders Sand',
      desc: 'Clean, coarse sand that spreads and levels easily. Perfect as a setting bed under pavers, pools, and structures.'
    }
  }]
}, {
  id: 'fill_type',
  dependsOn: {
    questionId: 'project',
    value: 'fill'
  },
  question: 'What do you need to accomplish?',
  options: [{
    label: "Raise grade or fill low spots (won't be planting)",
    value: 'grade',
    result: {
      name: 'Fill Dirt',
      desc: 'Our most affordable bulk material. Clean subsoil for raising grades, backfilling, and establishing drainage slopes.'
    }
  }, {
    label: 'Fill and then plant on top',
    value: 'plant',
    result: {
      name: 'Fill Dirt + 3-Way Topsoil',
      desc: 'Use Fill Dirt to build up the area, then top with 4-6 inches of 3-Way Topsoil for a plantable surface.'
    }
  }, {
    label: 'Affordable bulk fill for subgrade or earthwork',
    value: 'bulk',
    result: {
      name: 'Gravel Borrow',
      desc: 'A naturally occurring pit-run gravel blend that compacts well. Meets WSDOT Type 17 specs — great for building up subgrade, backfill, and general earthwork at a lower cost than crushed rock.'
    }
  }]
}];
// Map a quiz result name (which may contain alternatives like
// "Recycled Concrete or Recycled Asphalt" or "Fine, Medium, or Coarse Bark")
// to the first matching calcMaterials.id. Falls back to first material if
// no match is found.
function quizResultToMaterialId(resultName: string): string {
  if (!resultName) return calcMaterials[0].id;
  // Try exact match first
  const exact = calcMaterials.find(m => m.name === resultName);
  if (exact) return exact.id;
  // Then substring match — pick the first material whose name appears in
  // the result string. Sort by name length descending so longer names
  // (e.g. "1.25\" Minus") win over short prefixes (e.g. "1.25\" Clean").
  const sorted = [...calcMaterials].sort((a, b) => b.name.length - a.name.length);
  for (const m of sorted) {
    if (resultName.toLowerCase().includes(m.name.toLowerCase())) return m.id;
  }
  return calcMaterials[0].id;
}
export function HelpMeChoose() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Return-flow params: when the user came from the estimator or a product
  // page, we surface an "Apply X yards" button on the calculator result so
  // they can hand the calculated yardage back to where they started.
  const fromParam = searchParams.get('from'); // 'estimator' | 'product' | null
  const fromSlug = searchParams.get('slug');
  const fromAddress = searchParams.get('address');
  // Pull deep-link params (from Shop by Project page) up front so initial
  // state is correct on first render — no flash of default values.
  const initialProjectParam = searchParams.get('project');
  const initialMaterialParam = searchParams.get('material');
  const hasDeepLinkParams = Boolean(initialProjectParam || initialMaterialParam);
  const initialProjectType = initialProjectParam && projectTypes.some(p => p.id === initialProjectParam) ? initialProjectParam : projectTypes[0].id;
  // Look up the FULL project definition so the calculator can seed its
  // initial depth state with the project's default depth. Falls back to
  // the first projectType if nothing matches (shouldn't happen because
  // initialProjectType is always a valid id, but defensive anyway).
  const initialProjectDef = projectTypes.find(p => p.id === initialProjectType) ?? projectTypes[0];
  const initialMaterial = initialMaterialParam && calcMaterials.some(m => m.id === initialMaterialParam) ? initialMaterialParam : calcMaterials[0].id;
  const [activeTab, setActiveTab] = useState<'calculator' | 'quiz'>(searchParams.get('tab') === 'calculator' || hasDeepLinkParams ? 'calculator' : 'quiz');
  const [quizMaterial, setQuizMaterial] = useState(initialMaterial);
  // Calculator State
  const [projectType, setProjectType] = useState(initialProjectType);
  const [shape, setShape] = useState<Shape>('rectangle');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState(initialProjectDef.defaultDepth.toString());
  const [material, setMaterial] = useState(initialMaterial);
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  useEffect(() => {
    const selectedProject = projectTypes.find(p => p.id === projectType);
    if (selectedProject) setDepth(selectedProject.defaultDepth.toString());
  }, [projectType]);
  // Recalculate whenever shape changes so the user doesn't see stale numbers
  // from a previous shape's dimensions.
  useEffect(() => {
    setCalcResults(null);
  }, [shape]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const d = parseFloat(depth);
    if (isNaN(d) || d <= 0) return;
    let areaSqFt = 0;
    if (shape === 'rectangle' || shape === 'irregular') {
      const l = parseFloat(length);
      const w = parseFloat(width);
      if (isNaN(l) || isNaN(w)) return;
      areaSqFt = l * w;
    } else if (shape === 'circle') {
      const dia = parseFloat(diameter);
      if (isNaN(dia)) return;
      const r = dia / 2;
      areaSqFt = Math.PI * r * r;
    } else if (shape === 'triangle') {
      const b = parseFloat(base);
      const h = parseFloat(height);
      if (isNaN(b) || isNaN(h)) return;
      areaSqFt = 0.5 * b * h;
    }
    const depthInFeet = d / 12;
    const cubicFeet = areaSqFt * depthInFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  // Quiz State
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<{
    name: string;
    desc: string;
  } | null>(null);
  const currentQuizQuestion = quizQuestions.find(q => {
    if (!q.dependsOn) return !quizAnswers[q.id];
    return quizAnswers[q.dependsOn.questionId] === q.dependsOn.value && !quizAnswers[q.id];
  });
  // Calculate progress
  const totalSteps = 2; // Most paths are 2 steps
  const currentStep = quizResult ? 3 : Object.keys(quizAnswers).length + 1;
  const progressPercent = Math.min(currentStep / (totalSteps + 1) * 100, 100);
  const handleQuizAnswer = (questionId: string, value: string, result?: {
    name: string;
    desc: string;
  }) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (result) setQuizResult(result);
  };
  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const inputClass = 'w-full px-4 py-3 bg-stone-50 dark:bg-[#3a3a3a] border border-stone-200 dark:border-[#555555] rounded-xl text-sm text-brand-charcoal dark:text-white focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange outline-none transition-all appearance-none';
  const tabs = [{
    id: 'quiz' as const,
    label: 'Material Quiz',
    icon: HelpCircle
  }, {
    id: 'calculator' as const,
    label: 'Calculator',
    icon: Calculator
  }];
  // Hand the quiz recommendation to the calculator: switch tabs and
  // pre-fill the material dropdown with the closest calcMaterials match.
  const handleCalculateFromQuiz = () => {
    if (!quizResult) return;
    const matchedId = quizResultToMaterialId(quizResult.name);
    setQuizMaterial(matchedId);
    setActiveTab('calculator');
  };
  // Send the calculated yardage back to the source (estimator or product).
  // Estimator: returns to homepage with ?qty=N&material=NAME&address=...
  //   so the Hero can pre-fill all three fields and scroll into view.
  // Product: returns to /shop/<slug>?qty=N — Product.tsx already handles
  //   the qty param and either snaps the dropdown or stores it as a custom
  //   estimatorQty that splits cleanly across loads.
  const handleApplyToSource = (yards: number, materialName: string) => {
    if (yards < 1) return;
    if (fromParam === 'product' && fromSlug) {
      navigate(`/shop/${fromSlug}?qty=${yards}`);
      return;
    }
    if (fromParam === 'estimator') {
      const params = new URLSearchParams();
      params.set('qty', String(yards));
      if (materialName) params.set('material', materialName);
      if (fromAddress) params.set('address', fromAddress);
      navigate(`/?${params.toString()}#estimator`);
      return;
    }
  };
  return <main className="py-12 md:py-20 dark:bg-[#373737] min-h-[calc(100vh-64px)] transition-colors duration-300 bg-[#fcfcfc]" data-id="element-2688">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2689">
        {/* Header */}
        <div className="text-center mb-10" data-id="element-2690">
          <motion.h1 initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-2691">
            Help Me Choose
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-brand-muted dark:text-stone-300 text-sm md:text-base max-w-lg mx-auto" data-id="element-2692">
            Not sure which material you need or how much? Take our quick quiz or
            use the calculator.
          </motion.p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12" data-id="element-2693">
          <div className="relative flex gap-8" data-id="element-2694">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex items-center pb-2 text-sm font-semibold transition-colors duration-300 ${isActive ? 'text-brand-charcoal dark:text-white' : 'text-brand-muted dark:text-stone-300 hover:text-brand-charcoal dark:hover:text-white'}`} data-id="element-2695">
                  <tab.icon className="w-4 h-4 mr-2" data-id="element-2696" />
                  {tab.label}
                  {isActive && <motion.div className="absolute bottom-0 left-0 right-0 h-[3px] bg-brand-orange rounded-full" initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} data-id="element-2697" />}
                </button>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto" data-id="element-2698">
          <AnimatePresence mode="wait" data-id="element-2699">
            {/* QUIZ */}
            {activeTab === 'quiz' && <motion.div key="quiz" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-2700">
                <div className="bg-[#f5f5f5] dark:bg-[#474747] rounded-2xl border border-[#e5e5e5] dark:border-[#555555] shadow-sm overflow-hidden relative" data-id="element-2701">
                  {/* Progress Bar */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-[#e5e5e5] dark:bg-[#555555]" data-id="element-2702">
                    <motion.div className="h-full bg-brand-orange" initial={{
                  width: 0
                }} animate={{
                  width: `${progressPercent}%`
                }} transition={{
                  duration: 0.5,
                  ease: 'easeInOut'
                }} data-id="element-2703" />
                  </div>

                  <div className="p-8 md:p-12 bg-[#f5f5f5] dark:bg-[#424241]" data-id="element-2704">
                    {quizResult ? <motion.div initial={{
                  opacity: 0,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.4,
                  ease: [0.16, 1, 0.3, 1]
                }} className="text-center relative" data-id="element-2705">
                        {/* Solid green badge — bolder anchor than the
                         previous stone-50 ring. Tightened the bottom
                         margin so the badge feels connected to the
                         "We Recommend" eyebrow it introduces. */}
                        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2706">
                          <CheckCircle2 className="w-6 h-6 text-green-700 dark:text-green-400" data-id="element-2707" />
                        </div>
                        <h3 className="text-xs font-semibold tracking-widest uppercase text-brand-muted dark:text-stone-400 mb-2" data-id="element-2708">
                          We Recommend
                        </h3>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-4 tracking-tight" data-id="element-2709">
                          {quizResult.name}
                        </h2>
                        {/* Description darkened to stone-700 in light mode
                         for stronger body-copy contrast (~7:1 vs the
                         previous ~4.6:1 with stone-500). Dark-mode tone
                         unchanged — already readable. */}
                        <p className="text-stone-700 dark:text-stone-300 mb-10 max-w-md mx-auto leading-relaxed text-[15px]" data-id="element-2710">
                          {quizResult.desc}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-2711">
                          <button onClick={handleCalculateFromQuiz} className="inline-flex items-center justify-center px-6 py-2.5 bg-brand-orange text-white rounded-lg font-semibold text-sm hover:bg-brand-orange/90 transition-colors group" data-id="element-2712">
                            <Calculator className="w-4 h-4 mr-2" data-id="element-2713" />
                            Calculate How Much I Need
                            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" data-id="element-2714" />
                          </button>
                          <Link to="/#materials" className="inline-flex items-center justify-center px-6 py-2.5 bg-white dark:bg-[#3a3a3a] border border-[#e5e5e5] dark:border-[#555555] text-brand-charcoal dark:text-white rounded-lg font-medium text-sm hover:bg-[#eeeeee] dark:hover:bg-[#4a4a4a] transition-colors" data-id="element-2715">
                            Browse Materials
                          </Link>
                          <button onClick={resetQuiz} className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg font-medium text-sm text-brand-muted dark:text-stone-300 hover:text-brand-charcoal dark:hover:text-white transition-colors" data-id="element-2716">
                            <RefreshCw className="w-4 h-4 mr-2" data-id="element-2717" />
                            Start Over
                          </button>
                        </div>
                      </motion.div> : currentQuizQuestion ? <AnimatePresence mode="wait" data-id="element-2718">
                        <motion.div key={currentQuizQuestion.id} initial={{
                    opacity: 0,
                    x: 10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -10
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeOut'
                  }} data-id="element-2719">
                          <h3 className="font-heading text-xl md:text-2xl font-semibold text-brand-charcoal dark:text-white mb-8 text-center tracking-tight" data-id="element-2720">
                            {currentQuizQuestion.question}
                          </h3>
                          <div className="space-y-2.5 max-w-lg mx-auto" data-id="element-2721">
                            {currentQuizQuestion.options.map((option, index) => <motion.button key={option.value} initial={{
                        opacity: 0,
                        y: 5
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: index * 0.03
                      }} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full flex items-center justify-between p-4 rounded-xl border border-[#e5e5e5] dark:border-[#555555] dark:bg-[#3a3a3a] hover:border-brand-orange/50 dark:hover:border-brand-orange/50 hover:bg-[#eeeeee] dark:hover:bg-[#4a4a4a] transition-all group bg-white" data-id="element-2722">
                                  <span className="font-medium text-[15px] text-brand-charcoal dark:text-stone-200 group-hover:text-brand-orange transition-colors" data-id="element-2723">
                                    {option.label}
                                  </span>
                                  <ChevronRight className="w-4 h-4 text-stone-300 dark:text-stone-400 group-hover:text-brand-orange transition-colors" data-id="element-2724" />
                                </motion.button>)}
                          </div>
                        </motion.div>
                      </AnimatePresence> : null}
                  </div>
                </div>
              </motion.div>}

            {/* CALCULATOR */}
            {activeTab === 'calculator' && <motion.div key="calculator" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-2725">
                <MaterialCalculator variant="inline" initialMaterial={quizMaterial} initialProjectType={initialProjectType} onAddToCart={fromParam ? handleApplyToSource : undefined} data-id="element-2726" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </main>;
}